import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { GoogleCalendarSourceSettingsApi } from "./GoogleCalendarSourceSettings.Api";
import type { GoogleCalendarSourceSettingsViewModel } from "./GoogleCalendarSourceSettings.ViewModel.types";
import {
  GoogleCalendarEventColourIds,
  type GoogleCalendarConfiguration,
  type GoogleCalendarEventColourId,
  type GoogleCalendarRef,
} from "./GoogleCalendarSourceSettings.types";

const isColourId = (id: string | undefined): id is GoogleCalendarEventColourId =>
  id !== undefined && (GoogleCalendarEventColourIds as readonly string[]).includes(id);

const profileIdentity = (model: SourceSettingsModel): string =>
  `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;

export const useGoogleCalendarSourceSettingsViewModel = (
  model: SourceSettingsModel,
): GoogleCalendarSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const previousProfileIdentityRef = useRef(profileIdentity(model));

  const [calendars, setCalendars] = useState<GoogleCalendarRef[]>([]);
  const [calendarsLoaded, setCalendarsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.GoogleCalendar && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<GoogleCalendarConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const configurationRef = useRef(configuration);
  useEffect(() => {
    configurationRef.current = configuration;
  }, [configuration]);

  // A different Google account has different calendars; the old choice means nothing.
  const currentProfileIdentity = profileIdentity(model);
  useEffect(() => {
    const previous = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previous === currentProfileIdentity) {
      return;
    }

    setCalendars([]);
    updateConnectionConfiguration<GoogleCalendarConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({ ...cfg, calendars: [] }),
    );
  }, [currentProfileIdentity, isVisible]);

  useEffect(() => {
    const loadCalendars = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setCalendars([]);
        setCalendarsLoaded(true);
        return;
      }

      const api =
        sourceSettingsApiRef.current?.tryGetSourceApi<GoogleCalendarSourceSettingsApi>(
          SourceTypeIds.GoogleCalendar,
        );
      if (!api) {
        setCalendars([]);
        setCalendarsLoaded(true);
        return;
      }

      setCalendarsLoaded(false);
      setHasError(false);
      try {
        const nextCalendars = await api.getCalendars(model.boardId, editingConnectionId);
        setCalendars(nextCalendars);

        const primary = nextCalendars[0];
        const saved = configurationRef.current.calendars;
        if ((!saved || saved.length === 0) && primary) {
          // The server lists the primary calendar first. Selecting only it by default
          // keeps shared holiday and birthday calendars off the board until chosen.
          updateConnectionConfiguration<GoogleCalendarConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) =>
              cfg.calendars && cfg.calendars.length > 0
                ? cfg
                : { ...cfg, calendars: [primary] },
            { isDefault: true },
          );
        }
      } catch {
        setCalendars([]);
        setHasError(true);
      } finally {
        setCalendarsLoaded(true);
      }
    };

    void loadCalendars();
  }, [editingConnectionId, isVisible, model.boardId]);

  return {
    isVisible,
    calendars,
    calendarsLoaded,
    hasError,
    selectedCalendarIds: (configuration.calendars ?? []).map((c) => c.id),
    colourIds: GoogleCalendarEventColourIds,
    selectedColourId: isColourId(configuration.taskColorId)
      ? configuration.taskColorId
      : undefined,

    onSelectCalendars: (ids: string[]) => {
      const selected = calendars.filter((c) => ids.includes(c.id));
      updateConnectionConfiguration<GoogleCalendarConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({ ...cfg, calendars: selected }),
      );
    },

    onSelectColour: (id?: string) => {
      updateConnectionConfiguration<GoogleCalendarConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({ ...cfg, taskColorId: isColourId(id) ? id : undefined }),
      );
    },
  };
};
